import type { SizeLocales } from "@lib/constants/filtersConstants";
import { Attributes } from "@lib/enums/ProductAttributes";
import { getProductAttributeValue } from "@lib/utils/attributesUtils";
import { hasWindow } from "@lib/utils/hasWindow";
import { useStore } from "@store/index";
import { usePathname, useSearchParams } from "next/navigation";
import type { ProductVariant } from "@lib/product/types";
import { hasNotifySizeAvailability } from "@lib/utils/notificationFeature";
import { useCallback } from "react";

interface UseProductSizeHandlersParams {
    variants: ProductVariant[];
    currentlyUsedUnit: string;
    size: string | null;
    colorCode: string;
    setShowNotifyModal: (show: boolean) => void;
}

export interface UseProductSizeHandlersResult {
    handleSelectVariant: (args: { size: string; isAvailable: boolean }) => void;
    onSelectedVariantClick: (isAvailable: boolean, sizeData: string) => void;
    handleSizeSystemClick: (key: SizeLocales) => void;
    handleSizeClick: (isAvailable: boolean, sizeData: string) => void;
    updateUrlParameters: (selectedSize?: string, sizeSystem?: string) => void;
}

export const useProductSizeHandlers = ({
    variants,
    currentlyUsedUnit,
    size,
    colorCode,
    setShowNotifyModal,
}: UseProductSizeHandlersParams): UseProductSizeHandlersResult => {
    const activeProduct = useStore((state) => state.data.pdpState.activeProduct);
    const selectedVariant = activeProduct?.variant;
    const setActiveProduct = useStore((state) => state.setActiveProduct);
    const setGlobalShoeSizeConvention = useStore((state) => state.setGlobalShoeSizeConvention);
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const updateUrlParameters = useCallback(
        (selectedSize?: string, sizeSystem?: string) => {
            if (!hasWindow) return;

            const params = new URLSearchParams(searchParams.toString());

            if (selectedSize) {
                params.set("size", selectedSize);
            } else {
                params.delete("size");
            }

            if (sizeSystem) {
                params.set("system", sizeSystem);
            }

            const newUrl = `${pathname}?${params.toString()}`;
            window.history.replaceState({}, "", newUrl);
        },
        [pathname, searchParams]
    );

    const handleSelectVariant = ({ size, isAvailable }: { size: string; isAvailable: boolean }) => {
        if (!hasWindow) {
            return;
        }

        if (!size && selectedVariant !== null) {
            if (activeProduct) {
                setActiveProduct({
                    ...activeProduct,
                    variant: null,
                    isAvailable: false,
                });
            }
            return;
        }

        const currentSelectedVariant = variants.find((variant) => {
            if (variant === selectedVariant) {
                return false;
            }

            const variantSize = getProductAttributeValue(variant.attributesRaw, Attributes.size);
            return variantSize === size;
        });

        if (currentSelectedVariant) {
            setActiveProduct({
                colorCode: activeProduct?.colorCode ?? colorCode,
                variant: currentSelectedVariant,
                isAvailable,
            });
        }
        return;
    };

    const onSelectedVariantClick = useCallback(
        (isAvailable: boolean, sizeData: string) => {
            void handleSelectVariant({ size: sizeData, isAvailable });
            updateUrlParameters(sizeData, currentlyUsedUnit);
        },
        [handleSelectVariant, updateUrlParameters, currentlyUsedUnit]
    );

    const handleSizeSystemClick = useCallback(
        (key: SizeLocales) => {
            setGlobalShoeSizeConvention(key);
            updateUrlParameters(size, key);
        },
        [setGlobalShoeSizeConvention, updateUrlParameters, size]
    );

    const handleSizeClick = useCallback(
        (isAvailable: boolean, sizeData: string) => {
            onSelectedVariantClick(isAvailable, sizeData);
            if (isAvailable) {
                return;
            }

            if (hasNotifySizeAvailability) {
                setShowNotifyModal(true);
            }
        },
        [onSelectedVariantClick, setShowNotifyModal]
    );

    return {
        handleSelectVariant,
        onSelectedVariantClick,
        handleSizeSystemClick,
        handleSizeClick,
        updateUrlParameters,
    };
};
